import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  FiChevronLeft,
  FiChevronRight,
  FiClock,
  FiDollarSign,
  FiHome,
  FiRefreshCw,
  FiSearch,
  FiTrendingUp,
  FiUsers,
} from 'react-icons/fi';
import request from '../utils/request';

const SIGNAL_OPTIONS = [
  { value: 'early_check_in', label: 'Early check-ins' },
  { value: 'one_night_gap', label: '1-night gaps' },
  { value: 'two_night_gap', label: '2-night gaps' },
  { value: 'all', label: 'All signals' },
];

const SOURCE_OPTIONS = [
  { value: 'all', label: 'All PMS' },
  { value: 'streamline', label: 'Streamline' },
  { value: 'track', label: 'Track' },
  { value: 'guesty', label: 'Guesty' },
  { value: 'hospitable', label: 'Hospitable' },
];

function formatMoney(value) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(Math.round(Number(value || 0)));
}

function formatDate(value) {
  if (!value) return '-';
  const date = new Date(`${value}T00:00:00Z`);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

function formatDateTime(value) {
  if (!value) return 'Not computed yet';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Not computed yet';
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function signalLabel(signalType) {
  return SIGNAL_OPTIONS.find((option) => option.value === signalType)?.label || 'Signal';
}

function signalBadgeClass(signalType) {
  if (signalType === 'one_night_gap') return 'bg-amber-50 text-amber-700 border-amber-100';
  if (signalType === 'two_night_gap') return 'bg-orange-50 text-orange-700 border-orange-100';
  return 'bg-indigo-50 text-indigo-700 border-indigo-100';
}

function dateContext(row) {
  if (row.signalType === 'early_check_in') {
    return `Check-in ${formatDate(row.checkInDate || row.opportunityDate)}`;
  }
  return `${formatDate(row.previousCheckOutDate || row.opportunityDate)} to ${formatDate(row.nextCheckInDate)}`;
}

function StatCard({ label, value, detail, icon: Icon, iconClass = 'bg-indigo-50 text-indigo-600' }) {
  return (
    <div className="bg-white rounded-2xl border border-zinc-100 shadow-sm p-5">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-zinc-400">{label}</p>
          <p className="mt-2 text-2xl font-bold tracking-tight text-zinc-900">{value}</p>
          {detail && <p className="mt-1 text-xs text-zinc-500">{detail}</p>}
        </div>
        <div className={`h-10 w-10 rounded-xl flex items-center justify-center ${iconClass}`}>
          <Icon className="h-5 w-5" />
        </div>
      </div>
    </div>
  );
}

export default function EarlyCheckinsScreen() {
  const [signalType, setSignalType] = useState('early_check_in');
  const [source, setSource] = useState('all');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const pagination = analytics?.pagination || { page: 1, limit: 50, total: 0, pages: 1 };
  const rows = analytics?.rows || [];
  const summary = analytics?.summary || {};

  const fetchAnalytics = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({
        signalType,
        source,
        page: String(page),
        limit: '50',
      });
      if (search.trim()) params.set('q', search.trim());

      const { data } = await request.get(`/admin/analytics/early-checkins?${params.toString()}`);
      setAnalytics(data?.analytics || null);
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to load early check-in analytics.');
      setAnalytics(null);
    } finally {
      setLoading(false);
    }
  }, [page, search, signalType, source]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  const highLowRange = useMemo(() => {
    const low = formatMoney(summary.minPotentialRevenue || 0);
    const high = formatMoney(summary.maxPotentialRevenue || 0);
    return `${low} - ${high}`;
  }, [summary.maxPotentialRevenue, summary.minPotentialRevenue]);

  const handleSearch = (event) => {
    event.preventDefault();
    setPage(1);
    setSearch(searchInput);
  };

  const handleSignalChange = (nextSignalType) => {
    setPage(1);
    setSignalType(nextSignalType);
  };

  const handleSourceChange = (event) => {
    setPage(1);
    setSource(event.target.value);
  };

  return (
    <div className="flex-1 overflow-y-auto bg-zinc-50">
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="mb-7 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <div className="flex items-center gap-3">
              <div className="h-11 w-11 rounded-2xl bg-zinc-900 text-white flex items-center justify-center shadow-sm">
                <FiClock className="h-5 w-5" />
              </div>
              <div>
                <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-indigo-600">
                  Host analytics
                </p>
                <h1 className="text-2xl font-bold tracking-tight text-zinc-900">Early Checkins</h1>
              </div>
            </div>
            <p className="mt-3 max-w-2xl text-sm text-zinc-500">
              Drill into cached 2026 revenue opportunity signals by host, property, PMS, and signal type.
            </p>
          </div>

          <button
            type="button"
            onClick={fetchAnalytics}
            disabled={loading}
            className="inline-flex items-center justify-center gap-2 rounded-xl border border-zinc-200 bg-white px-4 py-2.5 text-sm font-semibold text-zinc-700 shadow-sm hover:bg-zinc-50 disabled:opacity-50"
          >
            <FiRefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
          <StatCard
            label="Signals"
            value={Number(summary.totalCount || 0).toLocaleString()}
            detail={signalLabel(signalType)}
            icon={FiTrendingUp}
            iconClass="bg-indigo-50 text-indigo-600"
          />
          <StatCard
            label="Hosts"
            value={Number(summary.hostCount || 0).toLocaleString()}
            detail="with cached signals"
            icon={FiUsers}
            iconClass="bg-sky-50 text-sky-600"
          />
          <StatCard
            label="Potential range"
            value={highLowRange}
            detail="low to high estimate"
            icon={FiDollarSign}
            iconClass="bg-emerald-50 text-emerald-600"
          />
          <StatCard
            label="Last computed"
            value={formatDateTime(summary.computedAt)}
            detail="from hostanalytics cache"
            icon={FiClock}
            iconClass="bg-amber-50 text-amber-600"
          />
        </div>

        <div className="mb-6 rounded-2xl border border-zinc-100 bg-white p-4 shadow-sm">
          <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
            <div className="flex flex-wrap gap-2">
              {SIGNAL_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => handleSignalChange(option.value)}
                  className={`rounded-xl px-3.5 py-2 text-sm font-semibold transition-colors ${
                    signalType === option.value
                      ? 'bg-zinc-900 text-white shadow-sm'
                      : 'bg-zinc-100 text-zinc-600 hover:bg-zinc-200'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>

            <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
              <select
                value={source}
                onChange={handleSourceChange}
                className="h-10 rounded-xl border border-zinc-200 bg-white px-3 text-sm font-medium text-zinc-700 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-100"
              >
                {SOURCE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>

              <form onSubmit={handleSearch} className="flex items-center gap-2">
                <div className="relative">
                  <FiSearch className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400" />
                  <input
                    value={searchInput}
                    onChange={(event) => setSearchInput(event.target.value)}
                    placeholder="Search host or home"
                    className="h-10 w-full min-w-[240px] rounded-xl border border-zinc-200 bg-white pl-9 pr-3 text-sm text-zinc-800 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-100"
                  />
                </div>
                <button
                  type="submit"
                  className="h-10 rounded-xl bg-indigo-600 px-4 text-sm font-semibold text-white hover:bg-indigo-700"
                >
                  Search
                </button>
              </form>
            </div>
          </div>
        </div>

        <div className="overflow-hidden rounded-2xl border border-zinc-100 bg-white shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-zinc-100 px-5 py-4">
            <div>
              <h2 className="text-base font-semibold text-zinc-900">Opportunity drilldown</h2>
              <p className="text-xs text-zinc-500">
                Cached rows only. No guest names, emails, raw reservation blobs, or event names are shown.
              </p>
            </div>
            <p className="text-xs font-semibold uppercase tracking-[0.14em] text-zinc-400">
              Page {pagination.page} of {pagination.pages}
            </p>
          </div>

          {error && (
            <div className="m-5 rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700">
              {error}
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="h-9 w-9 animate-spin rounded-full border-4 border-zinc-200 border-t-indigo-600" />
            </div>
          ) : rows.length === 0 ? (
            <div className="px-5 py-16 text-center">
              <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-zinc-100 text-zinc-400">
                <FiHome className="h-5 w-5" />
              </div>
              <p className="font-semibold text-zinc-900">No cached signals found</p>
              <p className="mt-1 text-sm text-zinc-500">
                Try another signal type, PMS filter, or search term.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-zinc-50/80 text-left text-[11px] font-bold uppercase tracking-[0.12em] text-zinc-400">
                    <th className="px-5 py-3">Signal</th>
                    <th className="px-5 py-3">Host</th>
                    <th className="px-5 py-3">Home</th>
                    <th className="px-5 py-3">Timing</th>
                    <th className="px-5 py-3">PMS / ADR</th>
                    <th className="px-5 py-3 text-right">Low</th>
                    <th className="px-5 py-3 text-right">High</th>
                    <th className="px-5 py-3 text-right">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-100">
                  {rows.map((row, index) => (
                    <tr key={`${row.hostId}-${row.propertyId}-${row.opportunityDate}-${index}`} className="hover:bg-zinc-50/60">
                      <td className="px-5 py-4 align-top">
                        <span className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-semibold ${signalBadgeClass(row.signalType)}`}>
                          {signalLabel(row.signalType)}
                        </span>
                      </td>
                      <td className="px-5 py-4 align-top">
                        <p className="font-semibold text-zinc-900">{row.companyName || row.hostName}</p>
                        {row.companyName && <p className="mt-0.5 text-xs text-zinc-500">{row.hostName}</p>}
                        <p className="mt-1 text-[11px] text-zinc-400">
                          Concierge {row.guestConciergeServicesEnabled ? 'enabled' : 'off'} - {row.conciergeSubscriptionStatus || 'none'}
                        </p>
                      </td>
                      <td className="px-5 py-4 align-top">
                        <p className="max-w-[260px] truncate font-medium text-zinc-800">{row.propertyLabel}</p>
                        {row.propertyId && <p className="mt-1 font-mono text-[11px] text-zinc-400">{row.propertyId}</p>}
                      </td>
                      <td className="px-5 py-4 align-top">
                        <p className="font-medium text-zinc-800">{dateContext(row)}</p>
                        <p className="mt-1 text-xs text-zinc-500">
                          Opportunity {formatDate(row.opportunityDate)}
                        </p>
                      </td>
                      <td className="px-5 py-4 align-top">
                        <p className="font-medium capitalize text-zinc-800">{row.source || '-'}</p>
                        <p className="mt-1 text-xs text-zinc-500">
                          ADR {row.adr ? formatMoney(row.adr) : '-'}
                        </p>
                      </td>
                      <td className="px-5 py-4 text-right align-top font-semibold text-zinc-700">
                        {formatMoney(row.minPotentialRevenue)}
                      </td>
                      <td className="px-5 py-4 text-right align-top font-bold text-zinc-900">
                        {formatMoney(row.maxPotentialRevenue)}
                      </td>
                      <td className="px-5 py-4 text-right align-top">
                        <Link
                          to={`/hostList/${row.hostId}`}
                          className="inline-flex items-center justify-center rounded-lg border border-zinc-200 px-3 py-1.5 text-xs font-semibold text-zinc-700 hover:bg-zinc-50"
                        >
                          View host
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-zinc-100 px-5 py-4">
            <p className="text-sm text-zinc-500">
              {pagination.total.toLocaleString()} total rows
            </p>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setPage((current) => Math.max(1, current - 1))}
                disabled={loading || pagination.page <= 1}
                className="inline-flex items-center gap-1.5 rounded-xl border border-zinc-200 px-3 py-2 text-sm font-semibold text-zinc-700 hover:bg-zinc-50 disabled:opacity-40"
              >
                <FiChevronLeft className="h-4 w-4" />
                Previous
              </button>
              <button
                type="button"
                onClick={() => setPage((current) => Math.min(pagination.pages, current + 1))}
                disabled={loading || pagination.page >= pagination.pages}
                className="inline-flex items-center gap-1.5 rounded-xl border border-zinc-200 px-3 py-2 text-sm font-semibold text-zinc-700 hover:bg-zinc-50 disabled:opacity-40"
              >
                Next
                <FiChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
